import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import MapArtist from '../shapes/MapArtist';
import { Container } from '@mui/material';
import { getZoomFromRadius } from './utils';

const defaultMapStyle = {
    width: '100%',
    height: '100vh%',
};

const apiConfig = {
    id: 'script-loader',
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_MAPS_KEY}&loading=async`,
    language: 'en',
    region: 'us',
    libraries: ['drawing', 'places'],
};

/**
 * Renders a Google Map using @react-google-maps/api, with optional clustering,
 * custom shapes, markers, polylines, and map controls.
 *
 * @param {Object} props - The props for the Map component.
 * @param {Array<Object>} props.data - Array of shape objects to render (polylines, circles, markers, etc).
 * @param {{lat: number, lng: number}} props.center - Initial center point of the map.
 * @param {Function} [props.onClick] - Callback for when the map itself is clicked.
 * @param {'roadmap' | 'satellite' | 'hybrid' | 'terrain'} props.mapTypeId - Type of map to render.
 * @param {Function} [props.handleEditCircleFromMap] - Callback fired when a circle is edited (drag/resized).
 * @param {string} [props.selectedShape] - ID of the currently selected shape.
 * @param {React.ReactNode} [props.conditionalControls] - Optional controls rendered in top-left overlay.
 * @param {Object} [props.mapStyle] - CSS-in-JS style object for the map container.
 * @param {Array<string>} [props.producerIds] - An array of producer ids, used to render MapCircle
 * @param {Array<string>} [props.processorIds] - An array of processor ids, used to render MapCircle
 * @param {Array<Object>} [props.trailerEvents] - Trailer to visualize on the map (e.g., drop trailer, pickup trailer).
 * @param {Array<Object>} [props.locationAnomalies] - Location anomalies to visualize on map (e.g., geofence departure, inactivity detected).
 * @param {number} [props.zoom=12] - Default zoom level.
 * @param {string|number} [props.height] - Optional override for map container height.
 * @param {boolean} [props.calculateZoomBasedOnPrimaryGeofence=false] - Whether to auto-zoom based on first shape's radius.
 * @param {Function} [props.handleViewChange] - Callback called when zoom or drag changes.
 * @param {Function} [props.handleMarkerClick] - Callback for clicking on a marker.
 * @param {string} [props.polylineStroke] - Optional stroke with to override the default polyline stroke (2).
 * @param {React.ReactNode} [props.children] - Any additional children to render inside the map container.
 * @param {React.ComponentType<{ renderFn: (clusterer?: any) => JSX.Element }>} [props.ClustererComponent]
 *     Optional React component that wraps a render function with a MarkerClusterer or similar. This component
 *     takes a prop `renderFn` which itself is a function of the signature `(clusterer) => JSX.Element`, and
 *     should return some JSX element (typically including a `<MarkerClusterer>`).
 *
 * @returns {JSX.Element|null} - A fully interactive Google Map with custom layers and optional clustering.
 */
const Map = ({
    data,
    center,
    onClick,
    mapTypeId,
    handleEditCircleFromMap = () => {},
    selectedShape,
    conditionalControls,
    mapStyle = defaultMapStyle,
    producerIds,
    processorIds,
    trailerEvents = [],
    locationAnomalies = [],
    zoom = 12,
    height,
    calculateZoomBasedOnPrimaryGeofence = false,
    handleViewChange = () => {},
    handleMarkerClick,
    polylineStroke,
    children,
    ClustererComponent = undefined,
    // wrap
}) => {
    const [centerPoint, setCenterPoint] = useState(center);
    const [zoomAdjusted, setZoomAdjusted] = useState(zoom);
    /**
     * Manage map instance to handle drag events
     */
    const [mapInstance, setMapInstance] = useState(null);

    /**
     * Manage shape instances to prevent re-rendering issues/crashes
     */
    const [circleInstances, setCircleInstances] = useState({});
    const [mapMarkerInstance, setMapMarkerInstances] = useState({});
    const [polylineInstances, setPolylineInstance] = useState({});
    const [markerInstances, setMarkerInstances] = useState({});

    useEffect(() => {
        if (mapInstance) {
            mapInstance.panTo(center);
            mapInstance.setZoom(zoomAdjusted);
        }
    }, [center]);

    useEffect(() => {
        if (mapInstance) {
            setZoomAdjusted(zoom);
        }
    }, [zoom]);

    const handleCircleLoad = useCallback((circle, shape) => {
        setCircleInstances((prevInstances) => {
            return { ...prevInstances, [shape._id]: circle };
        });
    });

    const handleMapMarkerLoad = useCallback((marker, shape) => {
        setMapMarkerInstances((prevInstances) => {
            return { ...prevInstances, [shape._id]: marker };
        });
    });

    const handlePolylineLoad = useCallback((polyline, shape) => {
        setPolylineInstance((prevInstances) => {
            return { ...prevInstances, [shape._id]: polyline };
        });
    });

    const handleMarkerLoad = useCallback((marker, shape) => {
        setMarkerInstances((prevInstances) => {
            return { ...prevInstances, [shape.id]: marker };
        });
    });

    const handleDragEnd = () => {
        if (mapInstance) {
            const newCenter = {
                lat: mapInstance.getCenter().lat(),
                lng: mapInstance.getCenter().lng(),
            };
            setCenterPoint(newCenter);
            handleViewChange(mapInstance);
        }
    };

    const handleZoomChanged = () => {
        if (mapInstance) {
            handleViewChange(mapInstance);
        }
    };

    const onMapLoad = useCallback((map) => {
        setMapInstance(map);
        if (calculateZoomBasedOnPrimaryGeofence) {
            const shape = data[0];
            if (!shape) return;
            const { radius_meters } = shape;
            setZoomAdjusted(getZoomFromRadius(radius_meters));
        }
    });

    const { isLoaded } = useJsApiLoader(apiConfig);

    if (!isLoaded) return null;

    const mapArtist = (clusterer) => (
        <MapArtist
            shapes={data}
            selectedShape={selectedShape}
            handleEditCircleFromMap={handleEditCircleFromMap}
            handleCircleLoad={handleCircleLoad}
            handleMarkerLoad={handleMarkerLoad}
            handleMarkerClick={handleMarkerClick}
            circleInstances={circleInstances}
            handlePolylineLoad={handlePolylineLoad}
            handleMapMarkerLoad={handleMapMarkerLoad}
            producerIds={producerIds}
            processorIds={processorIds}
            trailerEvents={trailerEvents}
            locationAnomalies={locationAnomalies}
            markerInstances={markerInstances}
            polylineStroke={polylineStroke}
            clusterer={clusterer}
        />
    );

    return (
        <GoogleMap
            mapContainerStyle={height ? { ...mapStyle, height } : mapStyle}
            center={centerPoint} // center should pull from the region location or Primary geofence
            zoom={zoomAdjusted}
            clickableIcons={false}
            onClick={onClick} // change cursor based on clickFunction
            mapTypeId={mapTypeId} // roadmap, satellite, hybrid, terrain
            onDragEnd={handleDragEnd}
            onZoomChanged={handleZoomChanged}
            onLoad={onMapLoad}
        >
            <Container style={{ position: 'absolute' }}>
                {selectedShape && conditionalControls ? conditionalControls : null}
                {/* wrap */}
            </Container>
            {ClustererComponent ? <ClustererComponent renderFn={mapArtist} /> : mapArtist(undefined)}
            {children}
        </GoogleMap>
    );
};

export default Map;
